import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/react-router';
import { FiCopy } from 'react-icons/fi';
import { IoIosCheckmarkCircleOutline } from 'react-icons/io';

import { client } from '../../client';
import DashboardLayout from '../layout/DashboardLayout';
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui';
import './index.css';

const BirthdayDashboardPage = () => {
  const navigate = useNavigate();
  const { isLoaded, userId } = useAuth();
  const [birthdayPages, setBirthdayPages] = useState([]);
  const [copiedPageIds, setCopiedPageIds] = useState(new Set());

  useEffect(() => {
    if (!isLoaded) {
      return;
    }
    if (!userId) {
      navigate('/');
      return;
    }

    client
      .fetch('*[_type == "birthdayPage" && user._ref == $userId] | order(_createdAt desc)', { userId })
      .then((data) => setBirthdayPages(data || []));
  }, [isLoaded, navigate, userId]);

  const publishedCount = useMemo(
    () => birthdayPages.filter((page) => page.status === 'published').length,
    [birthdayPages]
  );

  const handleCopy = async (id) => {
    const link = `${window.location.origin}/birthday/${id}`;
    await navigator.clipboard.writeText(link);
    setCopiedPageIds((prev) => new Set(prev).add(id));
  };

  return (
    <DashboardLayout
      title="Birthday Pages"
      subtitle="Create and manage personalized birthday experiences."
      actions={
        <Button size="sm" onClick={() => navigate('/dashboard/birthday/create')}>
          Create Birthday Page
        </Button>
      }
    >
      <section className="birthday-stats-grid">
        <Card>
          <CardHeader>
            <CardTitle>Total Pages</CardTitle>
            <CardDescription>All birthday pages you created</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="birthday-stat-value">{birthdayPages.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Published</CardTitle>
            <CardDescription>Ready to share with friends and family</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="birthday-stat-value">{publishedCount}</p>
          </CardContent>
        </Card>
      </section>

      <Card>
        <CardHeader>
          <CardTitle>Your Birthday Links</CardTitle>
          <CardDescription>Copy and share public links instantly.</CardDescription>
        </CardHeader>
        <CardContent>
          {birthdayPages.length === 0 ? (
            <p className="ui-muted">No birthday pages yet. Create your first one now.</p>
          ) : (
            <div className="birthday-list-table">
              <div className="birthday-list-header">
                <p>Recipient</p>
                <p>Birthday Date</p>
                <p>Status</p>
                <p>Action</p>
              </div>
              {birthdayPages.map((page) => (
                <div className="birthday-list-row" key={page._id}>
                  <p>{page.recipientName}</p>
                  <p>{page.birthdayDate ? new Date(page.birthdayDate).toLocaleDateString() : '-'}</p>
                  <div>{page.status === 'published' ? <Badge variant="secondary">Published</Badge> : <Badge variant="outline">Draft</Badge>}</div>
                  <div>
                    {copiedPageIds.has(page._id) ? (
                      <span className="birthday-copied">
                        <IoIosCheckmarkCircleOutline />
                        Copied
                      </span>
                    ) : (
                      <button type="button" className="birthday-copy-btn" onClick={() => handleCopy(page._id)}>
                        <FiCopy />
                        Copy link
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default BirthdayDashboardPage;
